import { ZOOM_CHAT_SDK_INIT_EVENT } from './zoomChat.constants';

/**
 * Access to Zoom's public campaign API, and the one thing we ask of it.
 *
 * `window.zoomCampaignSdk` DOES exist. A comment in this feature used to say it does not, and that
 * recorded a timing fact as an existence fact: the global is published by the campaign RUNTIME,
 * not by the loader we inject, and the runtime is a dynamic `import()` the loader performs. So the
 * global appears one or more network round trips AFTER our script tag lands, and any synchronous
 * read at mount finds nothing. Read out of the runtime itself:
 *
 *   Reflect.defineProperty(window, 'zoomCampaignSdk', { value: api, writable: false })
 *   window.dispatchEvent(new CustomEvent('zoomCampaignSdk:init'))
 *   // ...then, once chat, video and scheduled-callback init all settle:
 *   window.dispatchEvent(new CustomEvent('zoomCampaignSdk:ready'))
 *
 * `Reflect.defineProperty` defaults `configurable` to false, so once the runtime is up the global
 * can be neither replaced nor deleted — by us or by anything else sharing the page.
 *
 * Provenance: `web-campaign.js?v=9773`, SHA-256 b977800f9cbf7607, fetched 2026-08-13, and the
 * loader `zcc-sdk.js`, SHA-256 710823c7ff3be361 — byte-identical to the copy this integration was
 * first written against on 2026-08-07. Zoom serves both unversioned, so re-derive when the loader
 * hash moves.
 */

/**
 * Hands the SDK to a caller once it exists, and reports how to stop waiting.
 *
 * Both arrival orders have to be covered, because which one happens is not ours to decide: the
 * runtime may already be up when a caller starts waiting (a warm cache, or a second waiter), in
 * which case `zoomCampaignSdk:init` has already been dispatched and will never fire again. Waiting
 * only on the event would hang forever in exactly the case where the SDK is healthiest.
 */
export const onZoomChatSdkAvailable = (
  onAvailable: (sdk: ZoomCampaignSdk) => void
): (() => void) => {
  const alreadyPublished = window.zoomCampaignSdk;

  if (alreadyPublished) {
    onAvailable(alreadyPublished);

    return () => {};
  }

  const handleInit = () => {
    // Re-read rather than trusting the event: the event carries no payload, and it is the property
    // that is authoritative.
    const sdk = window.zoomCampaignSdk;

    if (sdk) {
      onAvailable(sdk);
    }
  };

  window.addEventListener(ZOOM_CHAT_SDK_INIT_EVENT, handleInit, { once: true });

  return () => window.removeEventListener(ZOOM_CHAT_SDK_INIT_EVENT, handleInit);
};

/**
 * Ends the live engagement, so a sign-out reaches the agent rather than only this browser.
 *
 * This is NOT what the eviction reload does, and the difference is the whole reason it exists.
 * `reloadToEvictZoomChatSdk` stops the client running in this document, and the storage sweep stops
 * the next patient inheriting the consumer record. Neither tells Zoom anything: the engagement
 * stays open in Contact Center, so the agent is left holding a live chat whose patient has signed
 * out, and anything they send lands in a transcript nobody will read. Ending it is the only thing
 * that closes the agent's side.
 *
 * Fire-and-forget on purpose. `endChat()` is a vendor round trip and sign-out must not wait on one
 * — the same rule the tracing call in `useLogout` already follows. Called before the navigation
 * rather than after, so the request goes out while the document is still healthy.
 *
 * Returns whether an engagement was actually ended, which is what makes the behaviour assertable;
 * the SDK's own `endChat()` resolves the same way whether or not there was anything to end.
 */
export const endZoomChatEngagement = (): boolean => {
  try {
    const sdk = window.zoomCampaignSdk;

    // Checked here rather than left to the SDK: its `endChat()` is a documented no-op when nothing
    // is live (it logs "chatSDK not ready" and resolves), so without this the return value could
    // not distinguish "ended an engagement" from "there was none".
    if (!sdk?.isChatting) {
      return false;
    }

    // `Promise.resolve` wraps a vendor return value we do not control the shape of, so a
    // synchronous throw and a rejected promise take the same path. An unhandled rejection during
    // sign-out would surface as a crash in a flow that has already succeeded.
    void Promise.resolve(sdk.endChat()).catch(() => {
      // Nothing actionable: the session is ending either way, and the agent-side timeout closes
      // an engagement we failed to end.
    });

    return true;
  } catch {
    // Signing out must never fail because a vendor global was in an unexpected state.
    return false;
  }
};
